import React, { useState, useEffect, useCallback } from 'react';
import { X, CalendarOff, Plus, Trash2, AlertTriangle, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import api from '../utils/api';

const formatDateDisplay = (dateStr) => {
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
};

const isSingleDay = (block) => block.start_date === block.end_date;

export default function AvailabilityModal({ onClose }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null); // { conflicting_services: [] }
  const [deletingId, setDeletingId] = useState(null);
  const [hasProfile, setHasProfile] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/availability');
      setBlocks(res.data.blocks || []);
      setHasProfile(res.data.member_id !== null);
    } catch {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!startDate) return;
    setSaving(true);
    setConflict(null);
    try {
      const res = await api.post('/availability', {
        start_date: startDate,
        end_date: endDate || startDate,
        reason: reason.trim(),
      });
      if (res.data.conflict) {
        setConflict(res.data);
      } else {
        setBlocks(prev => [...prev, res.data.block].sort((a, b) => a.start_date.localeCompare(b.start_date)));
        setStartDate('');
        setEndDate('');
        setReason('');
        toast.success('Unavailable date blocked');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blockId) => {
    setDeletingId(blockId);
    try {
      await api.delete(`/availability/${blockId}`);
      setBlocks(prev => prev.filter(b => b.block_id !== blockId));
      toast.success('Block removed');
    } catch {
      toast.error('Failed to remove');
    } finally {
      setDeletingId(null);
    }
  };

  // Today in local time as YYYY-MM-DD min value
  const _t = new Date();
  const todayStr = `${_t.getFullYear()}-${String(_t.getMonth()+1).padStart(2,'0')}-${String(_t.getDate()).padStart(2,'0')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <CalendarOff className="w-5 h-5 text-slate-500" />
            <div>
              <h2 className="text-base font-semibold text-slate-900">My Availability</h2>
              <p className="text-xs text-slate-500">Dates you won't be available for service</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="availability-modal-close" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {!hasProfile && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              Your account is not linked to a team member profile. Ask an administrator to add you to the team first.
            </div>
          )}

          {/* Conflict warning */}
          {conflict && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium text-rose-800">You have existing assignments on these dates</p>
              </div>
              <ul className="space-y-1 pl-6">
                {conflict.conflicting_services.map(s => (
                  <li key={s.service_id} className="text-sm text-rose-700 flex items-center gap-1.5">
                    <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    <span>{s.title}</span>
                    <span className="text-rose-500">({formatDateDisplay(s.date)})</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-rose-600 pl-1">Please decline these assignments first, then try again.</p>
            </div>
          )}

          {/* Add new block form */}
          {hasProfile && (
            <form onSubmit={handleAdd} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Block New Dates</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">From <span className="text-rose-500">*</span></label>
                  <input
                    type="date"
                    required
                    data-testid="availability-start-date"
                    value={startDate}
                    min={todayStr}
                    onChange={e => { setStartDate(e.target.value); setConflict(null); if (endDate && e.target.value > endDate) setEndDate(''); }}
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">To <span className="text-slate-400">(optional)</span></label>
                  <input
                    type="date"
                    data-testid="availability-end-date"
                    value={endDate}
                    min={startDate || todayStr}
                    onChange={e => { setEndDate(e.target.value); setConflict(null); }}
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Reason <span className="text-slate-400">(optional)</span></label>
                <input
                  type="text"
                  data-testid="availability-reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Out of town, Medical leave…"
                  maxLength={100}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={saving || !startDate}
                data-testid="availability-add-btn"
                className="w-full h-9 rounded-lg bg-green-700 text-white text-sm font-semibold hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {saving ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <><Plus className="w-3.5 h-3.5" /> Block Date(s)</>
                )}
              </button>
            </form>
          )}

          {/* Existing blocks */}
          <div>
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Your Blocked Dates</p>
            {loading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700 mx-auto" />
              </div>
            ) : blocks.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No blocked dates — you're available for all services
              </div>
            ) : (
              <ul className="space-y-2" data-testid="availability-blocks-list">
                {blocks.map(block => (
                  <li key={block.block_id} data-testid={`availability-block-${block.block_id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">
                        {isSingleDay(block)
                          ? formatDateDisplay(block.start_date)
                          : `${formatDateDisplay(block.start_date)} – ${formatDateDisplay(block.end_date)}`}
                      </p>
                      {block.reason && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{block.reason}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(block.block_id)}
                      disabled={deletingId === block.block_id}
                      data-testid={`delete-block-${block.block_id}`}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors flex-shrink-0 disabled:opacity-40"
                    >
                      {deletingId === block.block_id
                        ? <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin block" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
